import React from 'react';
import { Stack, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { ProfileImage } from 'new/elements/ProfileImage';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import { ReactComponent as NexsetListingRequestStar } from 'new/components/icons/NexsetListingRequestStar.svg';
import { ReactComponent as NexsetsIcon } from 'new/components/icons/nexsetsOutline.svg';
import { IconButton } from 'new/elements/IconButton/IconButton';
import { ChipWithCopy } from 'new/elements/ChipWithCopy/ChipWithCopy';
import PipelineStatusBadge, {
  Status,
} from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import { DomainItemActions } from '../../DomainItemActions';
import DomainItemDetailAction from '../../DomainItemDetailAction';
import ItemRating from '../../ItemRating';
import { Dataset } from '../types';
import styles from './DataProductDrawerHeader.module.scss';

export interface DataProductDrawerHeaderConfig {
  showOwner?: boolean;
  showRating?: boolean;
  showDomainActions?: boolean;
  showChipId?: boolean;
  showAsDataProduct?: boolean;
  showStatus?: boolean;
}

export const DrawerHeaderPresets = {
  MARKETPLACE_CONTEXT: {
    showOwner: true,
    showRating: true,
    showDomainActions: true,
    showChipId: false,
    showAsDataProduct: true,
    showStatus: false,
  },
  DEFAULT: {
    showOwner: false,
    showRating: false,
    showDomainActions: false,
    showChipId: true,
    showAsDataProduct: false,
    showStatus: true,
  },
} as const;

interface DataProductDrawerHeaderProps {
  dataset: Dataset;
  domainId?: number;
  isRequest?: boolean;
  isCustodian?: boolean;
  onClose: () => void;
  config?: DataProductDrawerHeaderConfig;
}

export const DataProductDrawerHeader: React.FC<DataProductDrawerHeaderProps> = ({
  dataset,
  domainId,
  isRequest = false,
  isCustodian,
  onClose,
  config = DrawerHeaderPresets.MARKETPLACE_CONTEXT,
}) => {
  const {
    showOwner = true,
    showRating = true,
    showDomainActions = true,
    showChipId = false,
    showAsDataProduct = true,
    showStatus = false,
  } = config;

  const finalShowRating = !isRequest && showRating;
  const finalShowDomainActions = !isRequest && showDomainActions;

  return (
    <div className={styles.topBar}>
      <Stack direction="row" alignItems="center" gap="16px">
        <div className={styles.boxInfo}>
          <div className={styles.connectionContainer}>
            <ConnectionTypeChip
              icon={showAsDataProduct ? <NexsetListingRequestStar /> : <NexsetsIcon />}
              label={showAsDataProduct ? 'Data Product' : 'Shared Nexset'}
            />
          </div>
        </div>
        {showChipId && (
          <ChipWithCopy tooltipTitle="Copy Dataset ID" toCopy={String(dataset.id)}>
            ID {dataset.id}
          </ChipWithCopy>
        )}
        {showStatus && (
          <PipelineStatusBadge
            id={dataset.id}
            status={dataset.status as Status}
            runtimeStatus={(dataset as any)?.runtime_status}
            ingestionMode={(dataset as any)?.ingestion_mode}
            parentArchivalStatus={(dataset as any)?.archival_status}
          />
        )}
        {showOwner && (
          <div className={styles.boxInfo}>
            <Typography variant="body3">Owned by</Typography>
            <ProfileImage
              color={'rgba(0,0,0,0.5)'}
              size="xxsmall"
              fullName={dataset?.owner?.full_name}
            />
            <Typography variant="body3">{dataset?.owner?.full_name}</Typography>
          </div>
        )}
        {finalShowRating && <ItemRating item={dataset as any} domainId={domainId} />}
      </Stack>
      <Stack direction="row" alignItems="center">
        {finalShowDomainActions && (
          <DomainItemActions domainId={domainId} item={dataset as any} inDrawer />
        )}
        {isCustodian && !isRequest && showDomainActions && (
          <DomainItemDetailAction
            item={dataset as any}
            domainId={domainId}
            onCloseDrawer={onClose}
          />
        )}
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Stack>
    </div>
  );
};
